import moment from 'moment';
import {Weekdays} from '@/helpers/dateHelpers/Weekdays.enum';
import {PickerEntity, PickerItem, PickerOrigin, PickerSlot, PickerTone} from './entityPicker.types';

/** Avatar tone per entity, re-using the modern.css tone tokens so dark mode works */
export const ENTITY_TONE: Record<PickerEntity, PickerTone> = {
    employee: 'info',
    loginUser: 'info',
    student: 'good',
    parent: 'teal',
    room: 'teal',
    institution: 'purple',
    product: 'warn',
    course: 'muted',
    mailTemplate: 'info',
};

/** Icon instead of initials for non-person entities */
export const ENTITY_ICON: Partial<Record<PickerEntity, string>> = {
    room: 'mdi-door',
    institution: 'mdi-school-outline',
    product: 'mdi-package-variant-closed',
    course: 'mdi-calendar-clock',
    mailTemplate: 'mdi-email-outline',
};

export const WEEKDAY_SHORT: Record<Weekdays, string> = {
    [Weekdays.MONDAY]: 'Mo',
    [Weekdays.TUESDAY]: 'Di',
    [Weekdays.WEDNESDAY]: 'Mi',
    [Weekdays.THURSDAY]: 'Do',
    [Weekdays.FRIDAY]: 'Fr',
    [Weekdays.SATURDAY]: 'Sa',
    [Weekdays.SUNDAY]: 'So',
};

/** Two letters of a name ("Anna Maria Beck" -> "AB") */
export function initialsOf(name: string): string {
    const parts = (name || '').trim().split(/\s+/).filter(Boolean);
    if (!parts.length) {
        return '?';
    }
    const first = parts[0][0] || '';
    const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
    return `${first}${last}`.toUpperCase();
}

/** Air-line distance in km between two [lon, lat] points */
export function distanceKm(a: PickerOrigin, b: PickerOrigin): number {
    const rad = (deg: number) => (deg * Math.PI) / 180;
    const dLat = rad(b[1] - a[1]);
    const dLon = rad(b[0] - a[0]);
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a[1])) * Math.cos(rad(b[1])) * Math.sin(dLon / 2) ** 2;
    return 6371 * 2 * Math.asin(Math.sqrt(h));
}

/** Valid [lon, lat] (the backend stores [0, 0] for unknown addresses) */
export function isValidOrigin(coords: unknown): coords is [number, number] {
    return Array.isArray(coords) && coords.length === 2
        && typeof coords[0] === 'number' && typeof coords[1] === 'number'
        && !(coords[0] === 0 && coords[1] === 0);
}

export function formatKm(km: number): string {
    return `${km.toLocaleString('de-DE', {maximumFractionDigits: km < 10 ? 1 : 0})} km`;
}

export function formatMinutes(minutes: number): string {
    return moment().startOf('day').add(minutes, 'minutes').format('HH:mm');
}

/** "Di 14:00" */
export function formatSlot(slot: PickerSlot): string {
    return `${WEEKDAY_SHORT[slot.dayOfWeek]} ${formatMinutes(slot.startTime)}`;
}

/** Valid slot (weekday and a positive time range) */
export function isValidSlot(slot: PickerSlot | null | undefined): slot is PickerSlot {
    return !!slot && typeof slot.dayOfWeek === 'number'
        && typeof slot.startTime === 'number' && typeof slot.endTime === 'number'
        && !Number.isNaN(slot.startTime) && !Number.isNaN(slot.endTime)
        && slot.endTime > slot.startTime;
}

/** Slot from a calendar item ('YYYY-MM-DD HH:mm' start/end), e.g. the course being edited */
export function slotFromEvent(event: { start?: string, end?: string, _id?: unknown } | null | undefined): PickerSlot | null {
    if (!event || !event.start || !event.end) {
        return null;
    }
    const start = moment(event.start, 'YYYY-MM-DD HH:mm');
    const end = moment(event.end, 'YYYY-MM-DD HH:mm');
    if (!start.isValid() || !end.isValid()) {
        return null;
    }
    const slot: PickerSlot = {
        dayOfWeek: (start.isoWeekday() - 1) as Weekdays,
        startTime: start.diff(start.clone().startOf('day'), 'minutes'),
        endTime: end.diff(end.clone().startOf('day'), 'minutes'),
        excludeCourseIds: event._id ? [String(event._id)] : [],
    };
    return isValidSlot(slot) ? slot : null;
}

/** Course-like document with weekday and time range */
export interface SlotCourse {
    _id: unknown;
    dayOfWeek?: Weekdays;
    timeRange?: { startTime: number, endTime: number };
}

/** True when the course takes place on the slot's weekday and overlaps the slot's time */
export function overlapsSlot(course: SlotCourse, slot: PickerSlot): boolean {
    if (course.dayOfWeek !== slot.dayOfWeek || !course.timeRange) {
        return false;
    }
    if ((slot.excludeCourseIds || []).some((id) => String(id) === String(course._id))) {
        return false;
    }
    return course.timeRange.startTime < slot.endTime && course.timeRange.endTime > slot.startTime;
}

/** String id of an ObjectId or populated document */
export function idOf(value: unknown): string {
    if (value && typeof value === 'object' && '_id' in (value as Record<string, unknown>)) {
        return String((value as { _id: unknown })._id);
    }
    return String(value);
}

/** Stable sort by rank (undefined last), keeps backend order for equal ranks */
export function sortByRank(items: PickerItem[]): PickerItem[] {
    return items
        .map((item, index) => ({item, index}))
        .sort((a, b) => {
            const ra = typeof a.item.rank === 'number' ? a.item.rank : Number.POSITIVE_INFINITY;
            const rb = typeof b.item.rank === 'number' ? b.item.rank : Number.POSITIVE_INFINITY;
            return ra === rb ? a.index - b.index : ra - rb;
        })
        .map((entry) => entry.item);
}

/** Plural helper: plural(2, 'Kurs', 'Kurse') -> "2 Kurse" */
export function plural(count: number, one: string, many: string): string {
    return `${count} ${count === 1 ? one : many}`;
}
